import React, { useEffect, useState } from 'react';

/**
 * InstallPrompt — PWA install banner.
 *
 * Listens for the browser's `beforeinstallprompt` event (Android Chrome,
 * Edge, etc.) and shows a small dismissible banner that triggers the
 * native install dialog when clicked.
 *
 * On iOS Safari, beforeinstallprompt isn't fired — we instead show a
 * one-time hint with manual instructions ("Share → Add to Home Screen").
 *
 * Dismissal is persisted in localStorage so the banner doesn't reappear
 * after the user has either installed or explicitly dismissed it.
 */

const STORAGE_KEY = 'alphagrade_install_prompt_dismissed';
const SESSION_KEY = 'alphagrade_install_prompt_shown';
const isDesktop = () => typeof window !== 'undefined' && window.innerWidth >= 768;
const INSTALLED_KEY = 'alphagrade_pwa_installed';

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    // Don't show if already installed/standalone or previously dismissed
    if (isStandalone() || localStorage.getItem(INSTALLED_KEY) === '1') {
      return;
    }
    if (localStorage.getItem(STORAGE_KEY) === '1') {
      return;
    }

    // Once per browser session at most (it reappeared on every page load).
    try {
      if (sessionStorage.getItem(SESSION_KEY) === '1') return;
    } catch { /* storage blocked: fall through */ }
    const markShown = () => { try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ } };

    // Android/Desktop: catch the deferred prompt. On desktop, wait until the
    // visitor has been here a while: the instant bottom pop-up covered the
    // pricing and /energy cards (found 2026-11-02).
    let showTimer = null;
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      showTimer = setTimeout(() => { setVisible(true); markShown(); }, isDesktop() ? 20000 : 0);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Mark as installed if the appinstalled event fires
    const handleInstalled = () => {
      localStorage.setItem(INSTALLED_KEY, '1');
      setVisible(false);
      setShowIosHint(false);
    };
    window.addEventListener('appinstalled', handleInstalled);

    // iOS Safari fallback: no beforeinstallprompt, show manual hint after delay
    if (isIOS()) {
      const t = setTimeout(() => setShowIosHint(true), 3000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
        window.removeEventListener('appinstalled', handleInstalled);
      };
    }

    return () => {
      if (showTimer) clearTimeout(showTimer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
    setShowIosHint(false);
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      localStorage.setItem(INSTALLED_KEY, '1');
    } else {
      // User declined — don't pester
      localStorage.setItem(STORAGE_KEY, '1');
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible && !showIosHint) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        // Desktop: a small corner notice, clear of page content. Phones keep the
        // centered banner.
        ...(isDesktop()
          ? { right: 16, left: 'auto', transform: 'none', maxWidth: 300 }
          : { left: '50%', transform: 'translateX(-50%)' }),
        background: 'var(--bg-card, #1A1F2E)',
        border: '1px solid rgba(0,212,170,0.4)',
        borderRadius: 12,
        padding: '0.85rem 1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.85rem',
        boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
        zIndex: 9000,
        ...(isDesktop() ? {} : { maxWidth: 420 }),
        width: 'calc(100% - 32px)',
        fontSize: '0.85rem',
      }}
    >
      <span style={{ fontSize: 24 }}>📱</span>
      <div style={{ flex: 1, color: 'var(--text-primary, #F1F5F9)', lineHeight: 1.4 }}>
        {showIosHint ? (
          <>
            <strong>Install AlphaGrade</strong>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #94A3B8)', marginTop: 2 }}>
              Tap the Share button → Add to Home Screen
            </div>
          </>
        ) : (
          <>
            <strong>Install AlphaGrade</strong>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #94A3B8)', marginTop: 2 }}>
              Add to your home screen for one-tap access
            </div>
          </>
        )}
      </div>
      {visible && deferredPrompt && (
        <button
          onClick={handleInstall}
          style={{
            padding: '0.5rem 0.9rem',
            background: 'linear-gradient(135deg, #6495ED, #00D4AA)',
            color: '#0B0F19',
            border: 'none',
            borderRadius: 8,
            fontWeight: 700,
            fontSize: '0.78rem',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          Install
        </button>
      )}
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--text-muted, #64748B)',
          fontSize: 20,
          cursor: 'pointer',
          padding: 4,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}
